import { Analysis, AnalysisPlanning } from '@shared/models/analysis.model';

import { Address } from '@shared/models/address.model';

/** Address-only entries are map markers, never submitted as analyses. */
export type MapEntry = Partial<Analysis> & { analysisId: string; addressOnly?: boolean };

export function organizationMapEntries(addresses: Address[], analyses: Analysis[]): MapEntry[] {
  const byId = new Map(addresses.map(address => [address.addressId, address]));
  const represented = new Set(analyses.map(row => row.addressId ?? row.address?.addressId).filter(Boolean));
  return [
    ...analyses.map(row => ({ ...row, address: byId.get(row.addressId ?? row.address?.addressId) ?? row.address })),
    ...addresses.filter(address => !represented.has(address.addressId)).map(address => ({
      analysisId: `address:${address.addressId}`, addressId: address.addressId, address, addressOnly: true,
    })),
  ];
}

export function addressKey(row: MapEntry): string {
  return row.addressId || row.address?.addressId || (location(row)?.join(':') ?? row.analysisId);
}

export type Point = [number, number];
/** Focus a dominant address area initially; full-area reset still includes every point. */
export function initialViewportPoints(points: Point[]): Point[] {
  if (points.length < 10) return points;
  const limits = [0, 1].map(axis => {
    const values = points.map(point => point[axis]).sort((a, b) => a - b);
    const lower = values[Math.floor((values.length - 1) * .25)];
    const upper = values[Math.floor((values.length - 1) * .75)];
    const margin = (upper - lower) * 1.5;
    return [lower - margin, upper + margin];
  });
  const mainArea = points.filter(point => point.every((value, axis) => value >= limits[axis][0] && value <= limits[axis][1]));
  // Keep genuinely dispersed organizations fully in view, rather than choosing one city.
  return mainArea.length >= points.length * .9 ? mainArea : points;
}

export interface Building {
  id: number;
  ring: Point[];
  address?: { street: string; houseNumber: string; postalCode?: string };
}
export const CAPACITY_COLORS = ['#8eafff', '#5e88f5', '#3363ff', '#203eaa'];
export const UNKNOWN_COLOR = '#cbd0dd';

export function location(analysis: MapEntry): Point | null {
  const lat = analysis.address?.latitude;
  const lng = analysis.address?.longitude;
  return typeof lat === 'number' && typeof lng === 'number' &&
    Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    ? [lat, lng] : null;
}

export function canLoadPlanning(analysis: MapEntry): boolean {
  if (analysis.addressOnly) return false;
  const stage = analysis.technicalStage ?? analysis.technicalStatus?.stage;
  const task = analysis.technicalTask ?? analysis.technicalStatus?.task ?? 0;
  return !!analysis.primaryPlanningId || !!analysis.plannings?.length || task >= 5 || stage === 'planning' || stage === 'complete';
}

export function primaryPlanning(analysis: MapEntry, plans: AnalysisPlanning[]): AnalysisPlanning | null {
  const created = (plan: AnalysisPlanning) => new Date(plan.createdAt).getTime() || 0;
  // Match the API's primary-planning rule even before a detail visit has saved its ID.
  const plan = plans.find(item => item.id === analysis.primaryPlanningId) ?? [...plans].sort((a, b) =>
    Number(b.status === 'completed') - Number(a.status === 'completed') ||
    created(b) - created(a) || a.id.localeCompare(b.id))[0];
  return plan?.status === 'completed' && plan.capacityAvailable !== false && Number.isFinite(plan.plantCapacity) && plan.plantCapacity >= 0 ? plan : null;
}

export function capacityColor(capacity: number | undefined): string {
  if (capacity === undefined || !Number.isFinite(capacity) || capacity < 0) return UNKNOWN_COLOR;
  return CAPACITY_COLORS[capacity < 5 ? 0 : capacity < 10 ? 1 : capacity < 20 ? 2 : 3];
}

export function contains(ring: Point[], [lat, lng]: Point): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i], [yj, xj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < (xj - xi) * (lat - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function parseBuildings(data: unknown): Building[] {
  if (!data || typeof data !== 'object' || !('elements' in data) || !Array.isArray(data.elements)) throw new Error('Invalid building response');
  const elements = data.elements.filter((value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object');
  const nodes = new Map<number, Point>();
  for (const element of elements) {
    if (element['type'] === 'node' && typeof element['id'] === 'number' && typeof element['lat'] === 'number' && typeof element['lon'] === 'number') {
      nodes.set(element['id'], [element['lat'], element['lon']]);
    }
  }
  // shortcut: closed OSM ways only; relations remain address points until a polygon provider is added.
  return elements.flatMap((element): Building[] => {
    const tags = element['tags'];
    if (element['type'] !== 'way' || typeof element['id'] !== 'number' || !tags || typeof tags !== 'object' || !('building' in tags) || tags.building === 'no') return [];
    if (!Array.isArray(element['nodes'])) return [];
    const ring: Point[] = [];
    for (const id of element['nodes']) {
      const point = nodes.get(id);
      if (!point || !Number.isFinite(point[0]) || !Number.isFinite(point[1]) || Math.abs(point[0]) > 90 || Math.abs(point[1]) > 180) return [];
      ring.push(point);
    }
    if (ring.length < 4 || ring[0][0] !== ring.at(-1)![0] || ring[0][1] !== ring.at(-1)![1]) return [];
    const addressTags = tags as Record<string, unknown>;
    const street = addressTags['addr:street'], houseNumber = addressTags['addr:housenumber'];
    const postalCode = addressTags['addr:postcode'];
    const address = typeof street === 'string' && typeof houseNumber === 'string'
      ? { street, houseNumber, ...(typeof postalCode === 'string' ? { postalCode } : {}) } : undefined;
    return [{ id: element['id'], ring, ...(address ? { address } : {}) }];
  });
}

export interface BuildingCell {
  key: string;
  south: number;
  west: number;
  north: number;
  east: number;
  rows: MapEntry[];
}

/** Small, stable extracts can be reused across zoom levels and pans. */
export function buildingCells(rows: MapEntry[]): BuildingCell[] {
  const cells = new Map<string, BuildingCell>();
  for (const row of rows) {
    const point = location(row);
    if (!point) continue;
    const y = Math.floor(point[0] / 0.005), x = Math.floor(point[1] / 0.01);
    const key = `${y}:${x}`;
    let cell = cells.get(key);
    if (!cell) {
      cell = { key, south: y * 0.005, west: x * 0.01, north: (y + 1) * 0.005, east: (x + 1) * 0.01, rows: [] };
      cells.set(key, cell);
    }
    cell.rows.push(row);
  }
  return [...cells.values()];
}

const ADDRESS_EDGE_TOLERANCE_METERS = 5;

function matchesBuildingAddress(building: Building, row: MapEntry): boolean {
  const address = building.address;
  const normalized = (value: string | undefined) => value?.normalize('NFKC').toLocaleLowerCase('de').replace(/ß/g, 'ss').replace(/\s+/g, ' ').trim();
  return !!address && !!row.address &&
    normalized(address.street) === normalized(row.address.street) &&
    normalized(address.houseNumber) === normalized(row.address.houseNumber) &&
    (!address.postalCode || !row.address.postalCode || normalized(address.postalCode) === normalized(row.address.postalCode));
}

function distanceToOutline(ring: Point[], [lat, lng]: Point): number {
  const lngScale = 111320 * Math.cos(lat * Math.PI / 180);
  let distance = Infinity;
  for (let i = 1; i < ring.length; i++) {
    const x = (ring[i - 1][1] - lng) * lngScale, y = (ring[i - 1][0] - lat) * 111320;
    const dx = (ring[i][1] - ring[i - 1][1]) * lngScale, dy = (ring[i][0] - ring[i - 1][0]) * 111320;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared ? Math.max(0, Math.min(1, -(x * dx + y * dy) / lengthSquared)) : 0;
    distance = Math.min(distance, Math.hypot(x + t * dx, y + t * dy));
  }
  return distance;
}

export function analysesInBuilding(building: Building, cells: BuildingCell[]): MapEntry[] {
  const south = Math.min(...building.ring.map(point => point[0]));
  const north = Math.max(...building.ring.map(point => point[0]));
  const west = Math.min(...building.ring.map(point => point[1]));
  const east = Math.max(...building.ring.map(point => point[1]));
  const latMargin = ADDRESS_EDGE_TOLERANCE_METERS / 111320;
  const lngMargin = latMargin / Math.max(.01, Math.cos((south + north) / 2 * Math.PI / 180));
  return cells.filter(cell => cell.south <= north + latMargin && cell.north >= south - latMargin && cell.west <= east + lngMargin && cell.east >= west - lngMargin)
    .flatMap(cell => cell.rows).filter(row => {
      const point = location(row)!;
      // Geocoded entrance pins may sit just outside; require matching OSM address tags.
      return contains(building.ring, point) || (matchesBuildingAddress(building, row) &&
        distanceToOutline(building.ring, point) <= ADDRESS_EDGE_TOLERANCE_METERS);
    });
}
